// Mock Supabase client for demo mode
// Implements the chaining query builder API so all existing component code works unchanged

import {
  getData, setData, rpcHandlers, edgeFunctionHandlers,
  demoUser, demoSession,
} from './demoData';
import { deactivateDemo } from './demoMode';

// ── MockQueryBuilder ──────────────────────────────────────────────
// Thenable chain that resolves against the in-memory demo data store

type Filter = { type: string; col: string; val: any; op?: string };

class MockQueryBuilder {
  private _table: string;
  private _filters: Filter[] = [];
  private _isSingle = false;
  private _isMaybeSingle = false;
  private _countMode = false;
  private _limit?: number;
  private _orderCol?: string;
  private _orderAsc?: boolean;
  private _writeOp?: 'insert' | 'update' | 'upsert' | 'delete';
  private _writeData?: any;

  constructor(table: string) {
    this._table = table;
  }

  select(_columns?: string, options?: { count?: string }) {
    if (options?.count === 'exact') this._countMode = true;
    return this;
  }

  eq(col: string, val: any) {
    this._filters.push({ type: 'eq', col, val });
    return this;
  }

  neq(col: string, val: any) {
    this._filters.push({ type: 'neq', col, val });
    return this;
  }

  in(col: string, vals: any[]) {
    this._filters.push({ type: 'in', col, val: vals });
    return this;
  }

  not(col: string, op: string, val: any) {
    this._filters.push({ type: 'not', col, val, op });
    return this;
  }

  is(col: string, val: any) {
    this._filters.push({ type: 'is', col, val });
    return this;
  }

  order(col: string, opts?: { ascending?: boolean }) {
    this._orderCol = col;
    this._orderAsc = opts?.ascending ?? true;
    return this;
  }

  limit(n: number) {
    this._limit = n;
    return this;
  }

  single() {
    this._isSingle = true;
    return this;
  }

  maybeSingle() {
    this._isMaybeSingle = true;
    return this;
  }

  insert(data: any) {
    this._writeOp = 'insert';
    this._writeData = data;
    return this;
  }

  update(data: any) {
    this._writeOp = 'update';
    this._writeData = data;
    return this;
  }

  upsert(data: any) {
    this._writeOp = 'upsert';
    this._writeData = data;
    return this;
  }

  delete() {
    this._writeOp = 'delete';
    return this;
  }

  // Make the builder thenable so `await` works
  then(
    resolve: (value: any) => any,
    reject?: (reason: any) => any
  ) {
    try {
      const result = this._resolve();
      return Promise.resolve(result).then(resolve, reject);
    } catch (e) {
      return reject ? Promise.reject(e).catch(reject) : Promise.reject(e);
    }
  }

  private _resolve(): any {
    // Handle write operations
    if (this._writeOp) {
      return this._handleWrite();
    }

    let data = [...getData(this._table)];

    // Apply filters
    for (const f of this._filters) {
      switch (f.type) {
        case 'eq':
          data = data.filter(row => row[f.col] === f.val);
          break;
        case 'neq':
          data = data.filter(row => row[f.col] !== f.val);
          break;
        case 'in':
          data = data.filter(row => (f.val as any[]).includes(row[f.col]));
          break;
        case 'not':
          if (f.op === 'is' && f.val === null) {
            data = data.filter(row => row[f.col] != null);
          }
          break;
        case 'is':
          if (f.val === null) {
            data = data.filter(row => row[f.col] == null);
          }
          break;
      }
    }

    // Apply ordering
    if (this._orderCol) {
      const col = this._orderCol;
      const asc = this._orderAsc;
      data.sort((a, b) => {
        if (a[col] < b[col]) return asc ? -1 : 1;
        if (a[col] > b[col]) return asc ? 1 : -1;
        return 0;
      });
    }

    // Apply limit
    if (this._limit != null) {
      data = data.slice(0, this._limit);
    }

    // Return shape
    if (this._isSingle) {
      return { data: data[0] || null, error: data.length === 0 ? { code: 'PGRST116', message: 'No rows found' } : null };
    }
    if (this._isMaybeSingle) {
      return { data: data[0] || null, error: null };
    }

    const result: any = { data, error: null };
    if (this._countMode) {
      result.count = data.length;
    }
    return result;
  }

  private _handleWrite(): any {
    const existing = getData(this._table);

    switch (this._writeOp) {
      case 'insert': {
        const rows = Array.isArray(this._writeData) ? this._writeData : [this._writeData];
        setData(this._table, [...existing, ...rows]);
        return { data: rows, error: null };
      }
      case 'update': {
        const updated = existing.map(row => {
          const match = this._filters.every(f => {
            if (f.type === 'eq') return row[f.col] === f.val;
            return true;
          });
          return match ? { ...row, ...this._writeData } : row;
        });
        setData(this._table, updated);
        return { data: null, error: null };
      }
      case 'upsert': {
        const rows = Array.isArray(this._writeData) ? this._writeData : [this._writeData];
        // Simple upsert: replace matching rows, add new ones
        const result = [...existing];
        for (const row of rows) {
          const idx = result.findIndex(r => r.id === row.id);
          if (idx >= 0) result[idx] = { ...result[idx], ...row };
          else result.push(row);
        }
        setData(this._table, result);
        return { data: rows, error: null };
      }
      case 'delete': {
        const filtered = existing.filter(row => {
          return !this._filters.every(f => {
            if (f.type === 'eq') return row[f.col] === f.val;
            return true;
          });
        });
        setData(this._table, filtered);
        return { data: null, error: null };
      }
      default:
        return { data: null, error: null };
    }
  }
}

// ── Mock auth ─────────────────────────────────────────────────────
const mockAuth = {
  getSession: () =>
    Promise.resolve({ data: { session: demoSession }, error: null }),

  getUser: () =>
    Promise.resolve({ data: { user: demoUser }, error: null }),

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    // Immediately fire SIGNED_IN so AuthContext picks up the demo session
    setTimeout(() => callback('SIGNED_IN', demoSession), 0);
    return { data: { subscription: { unsubscribe: () => {} } } };
  },

  signInWithOAuth: (_opts: any) =>
    Promise.resolve({ data: {}, error: null }),

  signOut: () => {
    deactivateDemo();
    return Promise.resolve({ error: null });
  },
};

// ── Mock functions ────────────────────────────────────────────────
const mockFunctions = {
  invoke: (funcName: string, options?: { body?: any }) => {
    const handler = edgeFunctionHandlers[funcName];
    if (handler) {
      const result = handler(options?.body || {});
      return Promise.resolve({ data: result, error: null });
    }
    return Promise.resolve({ data: null, error: null });
  },
};

// ── Mock storage ──────────────────────────────────────────────────
const mockStorage = {
  from: (_bucket: string) => ({
    upload: (_path: string, _file: any) => Promise.resolve({ data: { path: 'demo/uploaded.jpg' }, error: null }),
    getPublicUrl: (_path: string) => ({ data: { publicUrl: '' } }),
    remove: (_paths: string[]) => Promise.resolve({ data: null, error: null }),
    list: (_path: string) => Promise.resolve({ data: [], error: null }),
  }),
};

// ── Assembled demo client ─────────────────────────────────────────
export function createDemoClient() {
  return {
    from: (table: string) => new MockQueryBuilder(table),

    rpc: (funcName: string, params?: any) => {
      const handler = rpcHandlers[funcName];
      if (handler) {
        const result = handler(params || {});
        return Promise.resolve({ data: result, error: null });
      }
      return Promise.resolve({ data: null, error: null });
    },

    auth: mockAuth,
    functions: mockFunctions,
    storage: mockStorage,
  };
}
